import React, { useState } from 'react';
import { Table as BootstrapTable, Button, Modal, Form } from 'react-bootstrap';

interface SanctionItem {
  _id: string;
  Moderator: string;
  reason: string;
  type: string;
  date?: string;
  server?: string;
}

interface CasierData {
  _id: string;
  GuildID: string;
  id: string;
  sanction: SanctionItem[];
}

interface TableProps {
  sanctions?: CasierData[];
  onEdit?: (userId: string, sanctionId: string, data: any) => Promise<void>;
  onDelete?: (userId: string, sanctionId: string) => Promise<void>;
  canDelete?: boolean;
}

const Table: React.FC<TableProps> = ({ sanctions = [], onEdit, onDelete, canDelete = false }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSanction, setEditingSanction] = useState<SanctionItem & { userId: string; GuildID: string } | null>(null);
  const [editedReason, setEditedReason] = useState('');
  const [editedType, setEditedType] = useState('Warn');

  const sanctionLabels: Record<string, string> = {
    Note: 'Note',
    Warn: 'Avertissement',
    Mute: 'Mise en sourdine',
    Unmute: 'Fin de sourdine',
    Kick: 'Expulsion',
    Ban: 'Bannissement',
    Unban: 'Débannissement'
  };

  const formatDate = (date?: string): string => {
    try {

      if (!date) {
        return 'Date invalide';
      }

      const match = date.match(/<t:(\d+):R>/);
      if (!match) {
        console.warn('Format de date invalide :', date);
        return 'Date invalide';
      }

      const timestamp = parseInt(match[1], 10);
      if (isNaN(timestamp)) {
        console.warn('Horodatage invalide :', match[1]);
        return 'Date invalide';
      }

      const dateObj = new Date(timestamp * 1000);
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Paris'
      }).format(dateObj);

    } catch (error) {
      console.error('Erreur lors du formatage de la date :', error);
      return 'Date invalide';
    }
  };

  const handleEdit = (casier: CasierData, sanction: SanctionItem) => {
    setEditingSanction({ 
      ...sanction, 
      userId: casier.id, 
      GuildID: casier.GuildID 
    });
    setEditedReason(sanction.reason);
    setEditedType(sanction.type);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (editingSanction) {
      try {
        // Conserve la date d'origine pendant la modification.
        if (!onEdit) return;
        await onEdit(
          editingSanction.userId,
          editingSanction._id,
          {
            Moderator: editingSanction.Moderator,
            reason: editedReason,
            type: editedType,
            date: editingSanction.date,
            server: editingSanction.server,
            GuildID: editingSanction.GuildID
          }
        );
        setShowEditModal(false);
      } catch (error) {
        console.error("Erreur lors de l'enregistrement de la sanction :", error);
      }
    }
  };

  return (
    <>
      <BootstrapTable striped bordered hover>
        <thead>
          <tr>
            <th>Identifiant utilisateur</th>
            <th>Type</th>
            <th>Motif</th>
            <th>Modérateur</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sanctions.map(casier => 
            (casier.sanction ?? []).map(s => (
              <tr key={s._id}>
                <td>{casier.id}</td>
                <td>{sanctionLabels[s.type] ?? s.type}</td>
                <td>{s.reason}</td>
                <td>{s.Moderator}</td>
                <td>{formatDate(s.date)}</td>
                <td className="d-flex gap-2">
                  {onEdit && <Button
                    variant="warning" 
                    size="sm"
                    onClick={() => handleEdit(casier, s)}
                  >
                    Modifier
                  </Button>}
                  {canDelete && onDelete && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDelete(casier.id, s._id)}
                    >
                      Supprimer
                    </Button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </BootstrapTable>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier la sanction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select value={editedType} onChange={(event) => setEditedType(event.target.value)}>
                <option value="Note">Note</option>
                <option value="Warn">Avertissement</option>
                <option value="Mute">Mise en sourdine</option>
                <option value="Unmute">Fin de sourdine</option>
                <option value="Kick">Expulsion</option>
                <option value="Ban">Bannissement</option>
                <option value="Unban">Débannissement</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Motif</Form.Label>
              <Form.Control
                type="text"
                value={editedReason}
                onChange={(e) => setEditedReason(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Enregistrer les modifications
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Table;
